import React, { useEffect, useState } from "react";
import { ArrowLeft, X } from "lucide-react";
import { fetchWord } from "@/lib/api";

/**
 * variant: "sheet" (mobile/tablet full-screen overlay) | "panel" (desktop docked)
 */
export default function WordDetail({ token, onBack, onClose, onJumpVerse, variant = "sheet" }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        fetchWord(token.strongs, token.morph_code)
            .then(d => { if (alive) setData(d); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [token.strongs, token.morph_code]);

    const lex = data?.lexicon;
    const isPanel = variant === "panel";

    const body = (
        <>
            {!isPanel && (
                <div className="pt-2 pb-1 flex justify-center">
                    <div className="w-10 h-1.5 rounded-full bg-ink-10" />
                </div>
            )}

            <div className={"px-5 py-3 border-b border-ink-10 flex items-center justify-between " + (isPanel ? "pt-6" : "")}>
                <button
                    data-testid="word-back"
                    onClick={onBack}
                    className="flex items-center gap-1.5 font-ui text-[13px] text-ink-70 hover:text-ink transition"
                >
                    <ArrowLeft size={16} />
                    Back to verse
                </button>
                <div className="flex items-center gap-2">
                    <span className="font-ui text-[11px] uppercase tracking-[0.2em] text-moss-deep">
                        {token.strongs}
                    </span>
                    {isPanel && (
                        <button
                            onClick={onClose}
                            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-ink-10 transition"
                            aria-label="Close panel"
                        >
                            <X size={16} className="text-ink" />
                        </button>
                    )}
                </div>
            </div>

            <div className="px-6 py-5 overflow-y-auto sheet-scroll flex-1">
                <div className="pb-5 border-b border-ink-10 text-center">
                    <div className="font-ui text-[10px] uppercase tracking-[0.25em] text-moss-deep font-semibold">
                        Greek
                    </div>
                    <div className="mt-2 greek text-5xl md:text-6xl text-ink leading-none tracking-tight">
                        {token.greek}
                    </div>
                    <div className="mt-2 font-ui text-[14px] italic text-ink-70">{token.translit}</div>

                    {token.english && (
                        <div
                            data-testid="word-in-verse"
                            className="mt-4 inline-flex flex-col items-center gap-1 px-4 py-2 rounded-xl bg-sage-paper"
                        >
                            <span className="font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40">
                                Rendered in this verse
                            </span>
                            <span className="scripture text-[20px] md:text-[22px] text-ink font-medium leading-tight">
                                {String(token.english).replace(/[;,.\s]+$/g, "").trim()}
                            </span>
                        </div>
                    )}

                    {(token.gloss || lex?.gloss) && (
                        <p
                            data-testid="word-summary"
                            className="mt-4 scripture text-[17px] md:text-[18px] text-ink leading-snug"
                        >
                            <span className="font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40 block mb-1">
                                Meaning
                            </span>
                            {(token.gloss || lex?.gloss || "").replace(/^\s*to\s+/i, "to ")}
                        </p>
                    )}

                    {token.dict_form && (
                        <div className="mt-4 inline-flex items-center gap-2">
                            <span className="font-ui text-[10px] uppercase tracking-[0.2em] text-ink-40">
                                Dictionary form
                            </span>
                            <span className="greek text-lg text-ink-70">{token.dict_form}</span>
                        </div>
                    )}
                </div>

                <Section label="Morphology">
                    <div className="font-ui text-[12px] uppercase tracking-[0.18em] text-moss-deep mb-1">
                        {token.morph_code || "—"}
                    </div>
                    <div className="scripture text-[15px] text-ink leading-snug">
                        {loading ? "…" : (data?.morph_expansion || "Not decoded.")}
                    </div>
                </Section>

                <Section label="Full definition (TBESG lexicon)">
                    {loading && <p className="text-ink-40 text-sm">Loading…</p>}
                    {!loading && !lex && <p className="text-ink-40 text-sm">No lexicon entry found.</p>}
                    {lex && (
                        <>
                            <p className="scripture text-[16px] leading-[1.7] text-ink">
                                {lex.definition}
                            </p>
                            <div className="mt-3 font-ui text-[11px] text-ink-40">{lex.source}</div>
                        </>
                    )}
                </Section>

                <Section label={
                    loading
                        ? "Occurrences"
                        : `Occurs ${data?.occurrence_count ?? 0} time${(data?.occurrence_count ?? 0) === 1 ? "" : "s"} in Matthew`
                }>
                    {loading && <p className="text-ink-40 text-sm">…</p>}
                    {!loading && data?.occurrences?.length > 0 && (
                        <div className="flex flex-wrap gap-2 pb-4">
                            {data.occurrences.map(o => (
                                <button
                                    key={o.ref}
                                    data-testid={`occurrence-${o.chapter}-${o.verse}`}
                                    onClick={() => onJumpVerse(o.chapter, o.verse)}
                                    className="px-3 py-1.5 rounded-full bg-sage-paper text-ink font-ui text-[12px] hover:bg-moss/15 hover:text-moss-deep transition"
                                >
                                    Mt {o.chapter}:{o.verse}
                                </button>
                            ))}
                        </div>
                    )}
                </Section>
            </div>
        </>
    );

    if (isPanel) {
        return <div data-testid="word-detail" className="flex flex-col h-full">{body}</div>;
    }

    return (
        <div className="fixed inset-0 z-[60] animate-fade-in lg:hidden" data-testid="word-detail">
            <div className="absolute inset-0 bg-ink/45" onClick={onClose} />
            <div className="absolute inset-x-0 bottom-0 max-w-md md:max-w-lg mx-auto bg-white rounded-t-3xl shadow-[0_-8px_32px_rgba(30,43,38,0.2)] animate-sheet-in flex flex-col max-h-[92vh]">
                {body}
            </div>
        </div>
    );
}

function Section({ label, children }) {
    return (
        <section className="pt-5">
            <div className="font-ui text-[11px] uppercase tracking-[0.2em] text-ink-40 mb-2">{label}</div>
            {children}
        </section>
    );
}
